import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import {
  PaymentActivityPageViewModel,
  PaymentViewModel,
} from '@payments/models/payment-activity-page-view-model';
import {
  getPaymentActivityPageViewModel,
  getPaymentFiltersAndSortInformation,
  getPaymentViewModel,
  getSelectedPaymentCustomers,
} from '@payments/selectors/payments.selector';
import { FileHandlerPaymentsService } from '@payments/services/file-handler-payments.service';
import { ModalFilterService } from '@payments/services/payments-modal.service';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import {
  Filter,
  PaymentStoreService,
  SortDirection,
  SortFilter,
  paymentSelectors,
} from '@usf/ngrx-payment';
import { PaymentState } from '@usf/ngrx-payment/lib/models/state/payment.state';
import { PaymentActivitySortFields, Status } from '@usf/payment-types';
import { Observable, combineLatest } from 'rxjs';
import { filter, first, map, skipWhile, take, tap } from 'rxjs/operators';

export interface PaymentFilter {
  filterName: string;
  value: string | number | string[] | number[];
}

export enum PaymentFilterIds {
  referenceNumber = 'referenceNumber',
  paymentMethod = 'paymentMethod',
  paymentDate = 'paymentDate',
  toPaymentDate = 'toPaymentDate',
  fromPaymentDate = 'fromPaymentDate',
  status = 'status',
  customerPo = 'customerPO',
  transactionGroup = 'transactionGroupFilter',
  transactionNumber = 'transactionNumber',
  customers = 'customers',
}

@Injectable({
  providedIn: 'root',
})
export class PaymentActivityService implements ModalFilterService {
  readonly restrictToOpen = true;
  readonly initialSort = {
    fieldName: PaymentActivitySortFields.PAYMENT_DATE,
    sortValue: SortDirection.DESC,
  };

  constructor(
    private store: Store,
    private panAppState: PanAppState,
    readonly platform: PlatformService,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private router: Router,
    private paymentStore: PaymentStoreService,
    private fileHandlerService: FileHandlerPaymentsService,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {}

  getPaymentActivityPageViewModel$(): Observable<PaymentActivityPageViewModel> {
    return combineLatest([
      this.store.select(getPaymentActivityPageViewModel),
      this.panAppState.feature$([FEATURES.split_global_payments_outage.name]),
    ]).pipe(
      map(([vm, paymentHasOutage]) => {
        return {
          ...vm,
          paymentHasOutage,
        };
      }),
    );
  }

  getSelectedPaymentCustomers() {
    return combineLatest([
      this.store.select(getSelectedPaymentCustomers),
      this.store.select(getPaymentActivityPageViewModel),
    ]).pipe(
      map(([paymentCustomers, vm]) => {
        return {
          filters: vm.filters,
          paymentCustomers,
        };
      }),
    );
  }

  async presentPaymentOptions(buttons: any[], event?) {
    if (this.platform.platformType === PlatformEnum.desktop) {
      buttons.pop();
      const popover = await this.popoverController.create({
        component: EllipsisOptionsPopoverComponent,
        cssClass: 'ellipsis-options-popover-desktop',
        componentProps: {
          buttons,
        },
        mode: 'ios',
        event,
        showBackdrop: false,
      });
      return await popover.present();
    } else {
      const actionSheet = await this.actionSheetController.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons: buttons,
      });
      await actionSheet.present();
    }
  }

  clearFiltersAndResetSort() {
    this.setFilter([
      {
        filterName: PaymentFilterIds.fromPaymentDate,
        value: null,
      },
    ]);
    this.setSort([this.initialSort]);
  }

  setInitialSortAndFilter(initialFilters: Filter[] = []) {
    this.clearFiltersAndResetSort();

    const ninetyDays = 1000 * 60 * 60 * 24 * 89;
    const fourteenDays = 1000 * 60 * 60 * 24 * 14;
    const now = new Date();
    const fourteenDaysAhead = this.formatDate(
      new Date(now.valueOf() + fourteenDays),
    );
    const ninetyDaysAgo = this.formatDate(new Date(now.valueOf() - ninetyDays));

    const paymentFilters =
      initialFilters.length > 0
        ? initialFilters
        : [
            {
              filterName: PaymentFilterIds.fromPaymentDate,
              value: ninetyDaysAgo,
            },
            {
              filterName: PaymentFilterIds.toPaymentDate,
              value: fourteenDaysAhead,
            },
          ];

    const allCustomersIndex = paymentFilters.findIndex(
      filter =>
        filter.filterName === PaymentFilterIds.customers &&
        filter.value === '*',
    );
    if (allCustomersIndex >= 0) {
      const newFilters = paymentFilters.filter(
        invoiceFilter =>
          invoiceFilter.filterName !== PaymentFilterIds.customers,
      );
      this.setFilter(newFilters);
      this.setSort([this.initialSort]);
    } else {
      this.getSelectedPaymentCustomers()
        .pipe(
          first(
            ({ paymentCustomers }) => paymentCustomers.customers.length > 0,
          ),
        )
        .subscribe(({ paymentCustomers }) => {
          const paymentFiltersWithCustomers = [
            ...paymentFilters,
            {
              filterName: PaymentFilterIds.customers,
              value: paymentCustomers.customers,
            } as Filter,
          ];
          this.setFilter(paymentFiltersWithCustomers);
          this.setSort([this.initialSort]);
        });
    }
  }

  getFiltersAndSortInformation$(): Observable<{
    filters: PaymentFilter[];
    sort: SortFilter[];
  }> {
    return this.store.select(getPaymentFiltersAndSortInformation);
  }

  getStatusOptions(): Map<Status, string> {
    return new Map([
      [Status.PENDING, 'Pending'],
      [Status.PROCESSING, 'Processing'],
      [Status.CANCELLING, 'Cancelling'],
      [Status.CANCELLED, 'Cancelled'],
      [Status.PAID, 'Paid'],
    ]);
  }

  setSort(sortOrder: SortFilter[]) {
    this.paymentStore.setPaymentActivitySort(sortOrder);
  }

  setFilter(invoiceFilters: Filter[] | PaymentFilter[]): void {
    this.paymentStore.setPaymentActivityFilters(invoiceFilters);
  }

  canActivate(): Observable<boolean> {
    return combineLatest([
      this.panAppState.feature$([
        FEATURES.split_user_payments_payment_activity.name,
      ]),
      this.panAppState.user$,
    ]).pipe(
      filter(([, user]) => !!user),
      map(([flag]) => {
        !flag && this.router.navigate([PATHS.PAYMENTS]);
        return flag;
      }),
    );
  }

  downloadPaymentDetails(id: string) {
    this.getPaymentDetails(id, paymentViewModel => {
      this.fileHandlerService.downloadPaymentActivity([paymentViewModel]);
    });
  }

  downloadPaymentActivity(payments: PaymentViewModel[]) {
    this.fileHandlerService.downloadPaymentActivity(payments);
  }

  fetchPaymentActivity(
    pageNum: number,
    pageSize?: number,
    headerOnly: boolean = true,
  ) {
    this.paymentStore.getPaymentActivity(pageNum, pageSize, headerOnly);
  }

  sharePaymentActivity(
    payments: PaymentViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    this.fileHandlerService.sharePaymentActivity(payments, fromDate, toDate);
  }

  printPaymentDetails(id: string) {
    this.getPaymentDetails(id, paymentViewModel => {
      this.fileHandlerService.printPaymentDetails(paymentViewModel);
    });
  }

  printPaymentActivity(
    payments: PaymentViewModel[],
    fromDate?: Date,
    toDate?: Date,
  ) {
    this.fileHandlerService.printPaymentActivity(payments, fromDate, toDate);
  }

  reloadStatusFilterSummary() {
    this.paymentStore.reloadStatusSummary();
  }

  formatDate(date: Date): number {
    return +date.toJSON().replace(/-/g, '').substring(0, 8);
  }

  private getPaymentDetails(
    id: string,
    callback = (vm: PaymentViewModel) => {},
  ) {
    this.paymentStore.getPaymentDetails(id);
    this.store
      .select(paymentSelectors.selectPayment)
      .pipe(
        (src: Observable<{ loading: boolean }>) =>
          this.loadingSpinnerPipe(src) as Observable<PaymentState>,
        skipWhile(payments => !payments.error && !payments.paymentDetails),
        take(1),
      )
      .subscribe(payments => {
        const paymentViewModel = getPaymentViewModel(payments.paymentDetails);
        callback(paymentViewModel);
      });
  }

  private readonly loadingSpinnerPipe = (
    src: Observable<{ loading: boolean }>,
  ): Observable<any> => {
    return src.pipe(
      tap(
        payments =>
          payments.loading && this.loadingSpinnerService.createSpinnerModal(),
      ),
      filter(payments => !payments.loading),
      tap(async () => await this.loadingSpinnerService.dismissSpinnerModal()),
    );
  };
}
